import { useEffect, useState } from "react";
import { getAdminSettings, updateAdminSettings } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Save, LineChart, Megaphone, Info, CheckCircle2, AlertTriangle, ShoppingCart } from "lucide-react";
import { ADMIN } from "@/constants/testIds";

export const AdminSettings = ({ token }) => {
  const [form, setForm] = useState({ ga4_id: "", google_tag_id: "", google_ads_id: "", google_ads_label: "", google_ads_label_checkout: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getAdminSettings(token)
      .then((s) => setForm({
        ga4_id: s.ga4_id || "",
        google_tag_id: s.google_tag_id || "",
        google_ads_id: s.google_ads_id || "",
        google_ads_label: s.google_ads_label || "",
        google_ads_label_checkout: s.google_ads_label_checkout || "",
      }))
      .catch(() => toast.error("Errore nel caricamento delle impostazioni"))
      .finally(() => setLoading(false));
  }, [token]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await updateAdminSettings(token, form);
      toast.success("Impostazioni Google salvate. Ricarica il sito per applicare i tag.");
    } catch (e) {
      toast.error("Errore nel salvataggio delle impostazioni.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton className="h-80 rounded-2xl" />;

  const adsId = (form.google_ads_id || "").trim();
  const adsValid = /^AW-\d{6,}$/i.test(adsId);
  const purchaseActive = adsValid && (form.google_ads_label || "").trim().length > 0;
  const checkoutActive = adsValid && (form.google_ads_label_checkout || "").trim().length > 0;

  return (
    <div className="max-w-2xl space-y-5">
      <Alert className="rounded-2xl">
        <Info className="h-4 w-4" />
        <AlertTitle>Come funziona il tracciamento</AlertTitle>
        <AlertDescription>
          Inserisci qui gli ID di Google. Il tag (gtag.js) viene caricato automaticamente sul sito quando è presente almeno l'ID GA4 o Google Ads. Le <strong>conversioni Google Ads</strong> (inizio acquisto e acquisto completato, con valore in €) scattano da sole appena inserisci l'ID <code className="font-mono">AW-</code> e le relative etichette. Le modifiche si applicano al successivo caricamento delle pagine.
        </AlertDescription>
      </Alert>

      <Card className="rounded-2xl border-border p-6 glow">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><LineChart className="h-4 w-4" /></span>
          <h3 className="font-semibold">Google Tag &amp; Analytics</h3>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="gtag">Google Tag ID (consigliato)</Label>
            <Input id="gtag" data-testid={ADMIN.googleTag} value={form.google_tag_id} onChange={(e) => set("google_tag_id", e.target.value)} placeholder="GT-XXXXXXXX" className="rounded-xl font-mono" />
            <p className="text-xs text-muted-foreground">Tag unificato (<code className="font-mono">GT-</code>): instrada gli eventi a GA4 e Google Ads collegati.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ga4">ID misurazione GA4</Label>
            <Input id="ga4" data-testid={ADMIN.ga4} value={form.ga4_id} onChange={(e) => set("ga4_id", e.target.value)} placeholder="G-XXXXXXXXXX" className="rounded-xl font-mono" />
            <p className="text-xs text-muted-foreground">Usato come destinazione GA4 (se non hai un Google Tag).</p>
          </div>
        </div>
      </Card>

      <Card className="rounded-2xl border-border p-6 glow">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-accent"><Megaphone className="h-4 w-4" /></span>
            <h3 className="font-semibold">Google Ads</h3>
          </div>
          {adsId ? (
            adsValid ? (
              <Badge className="rounded-md gap-1 bg-emerald-500/15 text-emerald-600 border-emerald-500/30">
                <CheckCircle2 className="h-3 w-3" /> ID valido
              </Badge>
            ) : (
              <Badge className="rounded-md gap-1 bg-amber-500/15 text-amber-600 border-amber-500/30">
                <AlertTriangle className="h-3 w-3" /> Formato non valido
              </Badge>
            )
          ) : null}
        </div>

        <div className="mt-4 space-y-1.5">
          <Label htmlFor="ads">ID conversione Google Ads</Label>
          <Input id="ads" data-testid={ADMIN.adsId} value={form.google_ads_id} onChange={(e) => set("google_ads_id", e.target.value)} placeholder="AW-XXXXXXXXXX" className="rounded-xl font-mono" />
          <p className="text-xs text-muted-foreground">Formato: <code className="font-mono">AW-</code> seguito da cifre (es. <code className="font-mono">AW-1234567890</code>). È diverso dall'ID GA4.</p>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="ads-label">Etichetta conversione · Acquisto</Label>
              {purchaseActive && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
            </div>
            <Input id="ads-label" data-testid={ADMIN.adsLabel} value={form.google_ads_label} onChange={(e) => set("google_ads_label", e.target.value)} placeholder="abcdEFgh..." className="rounded-xl font-mono" />
            <p className="text-xs text-muted-foreground">Conversione primaria, scatta su /success.</p>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="ads-label-checkout">Etichetta conversione · Inizio checkout</Label>
              {checkoutActive && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
            </div>
            <Input id="ads-label-checkout" data-testid={ADMIN.adsLabelCheckout} value={form.google_ads_label_checkout} onChange={(e) => set("google_ads_label_checkout", e.target.value)} placeholder="ijklMNop..." className="rounded-xl font-mono" />
            <p className="text-xs text-muted-foreground">Opzionale, scatta al click su "Paga con PayPal".</p>
          </div>
        </div>

        <Alert className="mt-4 rounded-xl">
          <ShoppingCart className="h-4 w-4" />
          <AlertTitle>Eventi inviati automaticamente</AlertTitle>
          <AlertDescription className="text-xs">
            <strong>begin_checkout</strong> (click su PayPal) e <strong>purchase</strong> (pagina di conferma) con valore ordine in EUR. Funzionano già tramite il Google Tag / GA4. Due modi per contarle come conversioni Ads: <strong>(A)</strong> in Google Ads importa l'evento GA4 <code className="font-mono">purchase</code> come conversione (consigliato, nessun codice), oppure <strong>(B)</strong> inserisci qui ID <code className="font-mono">AW-</code> ed etichette per le conversioni dirette.
          </AlertDescription>
        </Alert>
      </Card>

      <Button onClick={save} disabled={saving} data-testid={ADMIN.saveSettings} className="rounded-xl glow-hover">
        <Save className="h-4 w-4" /> {saving ? "Salvataggio..." : "Salva impostazioni"}
      </Button>
    </div>
  );
};
